/**
 * Engine events -> language-neutral log messages.
 *
 * Shared by all three modes. The log stores *descriptors*, not sentences, so
 * the whole history re-reads itself in the other language the instant the
 * player switches: nothing here decides on words.
 *
 * It never shows information the player should not have: another seat's drawn
 * tile is logged as "1 tile", never named.
 */

import type { GameEvent, PlayerId } from "@kikik/mahjong-core";
import { claimParam, meldParam, msg, playerParam, tileParam } from "@kikik/i18n";
import type { Message } from "@kikik/i18n";

export interface LogEntry {
  id: number;
  message: Message;
  tone: "normal" | "you" | "claim" | "win" | "end";
}

export function describeEvent(event: GameEvent, you: PlayerId): Omit<LogEntry, "id"> | null {
  switch (event.type) {
    case "drew": {
      const who = playerParam(event.playerId);
      if (event.playerId === you) {
        return {
          tone: "you",
          message: msg(event.replacement ? "log.drew.you.replacement" : "log.drew.you", {
            player: who,
            tile: tileParam(event.tile.kind),
            wall: event.wallRemaining,
          }),
        };
      }
      return {
        tone: "normal",
        message: msg(event.replacement ? "log.drew.other.replacement" : "log.drew.other", {
          player: who,
        }),
      };
    }
    case "discarded":
      return {
        tone: event.playerId === you ? "you" : "normal",
        message: msg("log.discarded", {
          player: playerParam(event.playerId),
          tile: tileParam(event.tile.kind),
        }),
      };
    case "claim-window-opened":
      return {
        tone: "claim",
        message: msg("log.claimWindow", { tile: tileParam(event.discard.tile.kind) }),
      };
    case "claim-declared":
      return {
        tone: "claim",
        message: msg("log.claimDeclared", {
          player: playerParam(event.playerId),
          claim: claimParam(event.claim),
        }),
      };
    case "passed":
      return { tone: "normal", message: msg("log.passed", { player: playerParam(event.playerId) }) };
    case "meld-formed":
      return {
        tone: "claim",
        message: event.claimedTile
          ? msg("log.meldFormed", {
              player: playerParam(event.playerId),
              meld: meldParam(event.meld.kind),
              tile: tileParam(event.claimedTile.kind),
            })
          : msg("log.meldFormed.concealed", {
              player: playerParam(event.playerId),
              meld: meldParam(event.meld.kind),
            }),
      };
    case "won":
      return {
        tone: "win",
        message:
          event.by === "self-draw"
            ? msg("log.won.selfDraw", {
                player: playerParam(event.playerId),
                tile: tileParam(event.winningTile),
              })
            : msg("log.won.discard", {
                player: playerParam(event.playerId),
                from: playerParam(event.from ?? event.playerId),
                tile: tileParam(event.winningTile),
              }),
      };
    case "exhaustive-draw":
      return { tone: "end", message: msg("log.exhaustiveDraw") };
    case "turn-changed":
      return null;
    default:
      return null;
  }
}
